import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase/config';

import Login         from './pages/Login';
import AdvisorView   from './pages/AdvisorView';
import AdminDashboard from './pages/AdminDashboard';
import PartsManager  from './pages/PartsManager';
import Reports       from './pages/Reports';

const DEV_MODE = false;

const devNavStyle = {
  position: 'fixed', bottom: 16, right: 16, zIndex: 9999,
  background: '#161B27', border: '1px solid #F5A623', borderRadius: 10,
  padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6,
  boxShadow: '0 4px 24px rgba(0,0,0,0.5)'
};
const devLabelStyle = {
  fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: '#F5A623',
  textTransform: 'uppercase', marginBottom: 2, fontFamily: 'monospace'
};
const devBtnStyle = (active) => ({
  background: active ? '#F5A623' : 'transparent',
  color: active ? '#000' : '#94A3B8',
  border: '1px solid ' + (active ? '#F5A623' : '#252D3D'),
  borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'monospace', letterSpacing: 0.5
});

const roleToScreen = {
  admin:           'admin',
  service_manager: 'reports',
  parts_manager:   'parts',
  advisor:         'advisor',
};

const SCREENS = [
  { id: 'login',   label: '🔐 Login / Register',  component: Login           },
  { id: 'advisor', label: '🔍 Advisor View',       component: AdvisorView     },
  { id: 'admin',   label: '⚙️  Admin Dashboard',   component: AdminDashboard  },
  { id: 'parts',   label: '🔧 Parts Manager',      component: PartsManager    },
  { id: 'reports', label: '📈 Reports',            component: Reports         },
];

// ── Trial enforcement helpers ─────────────────────────────────
function getTrialStatus(storeData) {
  if (!storeData) return { status: 'loading' };
  const planStatus = storeData.planStatus || 'trial';
  const plan       = storeData.plan       || 'trial';

  if (planStatus === 'active') return { status: 'active', plan };
  if (planStatus === 'past_due') return { status: 'past_due', plan };
  if (planStatus === 'canceled') return { status: 'canceled', plan };

  // On trial — calculate days left
  const trialEnds = storeData.trialEnds?.toDate?.() || null;
  if (!trialEnds) return { status: 'trial', daysLeft: 30 };
  const daysLeft = Math.ceil((trialEnds - new Date()) / (1000 * 60 * 60 * 24));
  return { status: 'trial', daysLeft: Math.max(daysLeft, 0) };
}

function TrialBanner({ trialStatus, onNavigate }) {
  const { status, daysLeft } = trialStatus;

  if (status === 'active') return null;

  const base = {
    position: 'sticky', top: 0, zIndex: 200,
    padding: '10px 28px', display: 'flex', alignItems: 'center',
    gap: 12, fontSize: 13, fontFamily: "'DM Sans', sans-serif",
  };

  if (status === 'trial' && daysLeft > 7) return (
    <div style={{ ...base, background: 'rgba(34,197,94,0.08)', borderBottom: '1px solid rgba(34,197,94,0.2)' }}>
      <span>🎉</span>
      <span style={{ color: '#94A3B8' }}>
        <strong style={{ color: '#22C55E' }}>Free Trial — {daysLeft} days remaining.</strong>
        {' '}You have full Pro access. Subscribe before it ends to keep everything.
      </span>
      <button onClick={() => onNavigate?.('admin')}
        style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid rgba(34,197,94,0.4)', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 700, color: '#22C55E', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        View Plans →
      </button>
    </div>
  );

  if (status === 'trial' && daysLeft <= 7 && daysLeft > 0) return (
    <div style={{ ...base, background: 'rgba(245,166,35,0.08)', borderBottom: '1px solid rgba(245,166,35,0.3)' }}>
      <span>⚠️</span>
      <span style={{ color: '#94A3B8' }}>
        <strong style={{ color: '#F5A623' }}>Trial ends in {daysLeft} day{daysLeft !== 1 ? 's' : ''}.</strong>
        {' '}Subscribe now to avoid interruption.
      </span>
      <button onClick={() => onNavigate?.('admin')}
        style={{ marginLeft: 'auto', background: '#F5A623', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 700, color: '#000', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        Subscribe Now →
      </button>
    </div>
  );

  if (status === 'trial' && daysLeft <= 0) return (
    <div style={{ ...base, background: 'rgba(239,68,68,0.08)', borderBottom: '1px solid rgba(239,68,68,0.3)' }}>
      <span>🔴</span>
      <span style={{ color: '#94A3B8' }}>
        <strong style={{ color: '#EF4444' }}>Trial expired.</strong>
        {' '}Subscribe to restore full access.
      </span>
      <button onClick={() => onNavigate?.('admin')}
        style={{ marginLeft: 'auto', background: '#EF4444', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        Subscribe Now →
      </button>
    </div>
  );

  if (status === 'past_due') return (
    <div style={{ ...base, background: 'rgba(239,68,68,0.08)', borderBottom: '1px solid rgba(239,68,68,0.3)' }}>
      <span>⚠️</span>
      <span style={{ color: '#EF4444', fontWeight: 700 }}>Payment past due — please update your payment method to keep access.</span>
      <button onClick={() => onNavigate?.('admin')}
        style={{ marginLeft: 'auto', background: '#EF4444', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        Update Payment →
      </button>
    </div>
  );

  if (status === 'canceled') return (
    <div style={{ ...base, background: 'rgba(239,68,68,0.08)', borderBottom: '1px solid rgba(239,68,68,0.3)' }}>
      <span>🔴</span>
      <span style={{ color: '#EF4444', fontWeight: 700 }}>Subscription canceled — resubscribe to restore access.</span>
      <button onClick={() => onNavigate?.('admin')}
        style={{ marginLeft: 'auto', background: '#EF4444', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        Resubscribe →
      </button>
    </div>
  );

  return null;
}

function AuthLoading() {
  return (
    <div style={{ minHeight: '100vh', background: '#0E1117', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
      <div style={{ width: 48, height: 48, background: '#F5A623', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, color: '#000' }}>L</div>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, letterSpacing: 4, color: '#F1F5F9' }}>LANE<span style={{ color: '#F5A623' }}>IQ</span></div>
      <div style={{ width: 36, height: 36, border: '3px solid #252D3D', borderTopColor: '#F5A623', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function App() {
  const [screen,      setScreen]      = useState('login');
  const [currentUser, setCurrentUser] = useState(null);
  const [storeData,   setStoreData]   = useState(null);
  const [authReady,   setAuthReady]   = useState(false);
  const [checkoutMsg, setCheckoutMsg] = useState(null);

  // Handle Stripe redirect back to app
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success') {
      setCheckoutMsg({ type: 'success', text: '🎉 Subscription activated! Welcome to LaneIQ.' });
      window.history.replaceState({}, '', '/');
    } else if (checkout === 'canceled') {
      setCheckoutMsg({ type: 'warn', text: 'Checkout canceled — no charge was made.' });
      window.history.replaceState({}, '', '/');
    }
    if (checkout) setTimeout(() => setCheckoutMsg(null), 5000);
  }, []);

  // Load store data when user changes
  const loadStoreData = async (storeId) => {
    if (!storeId) return;
    try {
      const snap = await getDoc(doc(db, 'stores', storeId));
      if (snap.exists()) setStoreData(snap.data());
    } catch (e) { console.error('loadStoreData:', e); }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            const userData = { uid: firebaseUser.uid, ...userDoc.data() };
            if (userData.status === 'active') {
              setCurrentUser(userData);
              setScreen(roleToScreen[userData.role] || 'advisor');
              loadStoreData(userData.storeId);
            } else {
              setScreen('login');
            }
          } else {
            setScreen('login');
          }
        } catch (e) {
          console.error('Auth restore error:', e);
          setScreen('login');
        }
      } else {
        setScreen('login');
        setStoreData(null);
      }
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  const handleLogin = (userData) => {
    setCurrentUser(userData);
    setScreen(roleToScreen[userData.role] || 'advisor');
    loadStoreData(userData.storeId);
  };

  const handleLogout = async () => {
    await auth.signOut();
    setCurrentUser(null);
    setStoreData(null);
    setScreen('login');
  };

  const handleNavigate = (screenId) => {
    setScreen(screenId);
    // Refresh store data when navigating to billing
    if (screenId === 'admin' && currentUser?.storeId) {
      loadStoreData(currentUser.storeId);
    }
  };

  if (!authReady) return <AuthLoading />;

  const ActiveScreen = SCREENS.find(s => s.id === screen)?.component || Login;
  const trialStatus  = getTrialStatus(storeData);
  const isDemo       = currentUser?.email === 'demo@laneiq.io';

  return (
    <>
      {/* Checkout success / cancel toast */}
      {checkoutMsg && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#161B27', border: `1px solid ${checkoutMsg.type === 'success' ? '#22C55E' : '#F5A623'}`,
          borderRadius: 8, padding: '11px 20px', fontSize: 13, fontWeight: 600,
          color: checkoutMsg.type === 'success' ? '#22C55E' : '#F5A623',
          zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          fontFamily: "'DM Sans', sans-serif",
        }}>
          {checkoutMsg.text}
        </div>
      )}

      {/* Trial / billing status banner — shows on all screens when logged in */}
      {currentUser && screen !== 'login' && !isDemo && (
        <TrialBanner trialStatus={trialStatus} onNavigate={handleNavigate} />
      )}

      {/* Demo banner — shows when logged in as demo user */}
      {isDemo && screen !== 'login' && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 200,
          background: 'rgba(245,166,35,0.07)',
          borderBottom: '1px solid rgba(245,166,35,0.25)',
          padding: '9px 28px', display: 'flex', alignItems: 'center',
          gap: 12, fontSize: 12, fontFamily: "'DM Sans', sans-serif",
        }}>
          <span style={{ fontWeight: 700, color: '#F5A623', letterSpacing: '0.5px' }}>
            DEMO MODE
          </span>
          <span style={{ color: '#475569' }}>·</span>
          <span style={{ color: '#94A3B8' }}>
            Summit Auto Group · Pre-loaded data · Resets nightly
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, color: '#475569' }}>Ready to run this at your store?</span>
            <button
              onClick={handleLogout}
              style={{
                background: '#F5A623', border: 'none', borderRadius: 6,
                padding: '5px 14px', fontSize: 12, fontWeight: 700,
                color: '#000', cursor: 'pointer', whiteSpace: 'nowrap',
                fontFamily: "'DM Sans', sans-serif",
              }}>
              Start Free Trial →
            </button>
          </div>
        </div>
      )}

      <ActiveScreen
        onLogin={handleLogin}
        onLogout={handleLogout}
        currentUser={currentUser}
        storeData={storeData}
        onNavigate={handleNavigate}
      />

      {DEV_MODE && (
        <div style={devNavStyle}>
          <div style={devLabelStyle}>🛠 Dev Navigator</div>
          {SCREENS.map(s => (
            <button key={s.id} style={devBtnStyle(screen === s.id)} onClick={() => setScreen(s.id)}>
              {s.label}
            </button>
          ))}
          {currentUser && (
            <div style={{ fontSize: 10, color: '#475569', marginTop: 4, fontFamily: 'monospace', borderTop: '1px solid #252D3D', paddingTop: 6, lineHeight: 1.8 }}>
              👤 {currentUser.name}<br />
              🏷 {currentUser.role}<br />
              ✅ {currentUser.status}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default App;
